/**
 * Internal linking engine for SEO pages.
 * Every helper returns plain `{ label, href, rel? }` objects rendered as
 * real crawlable <a> tags by the consuming component.
 */

import { citySlug, specialtySlug, stateNameSlug } from "@/lib/seo-slugs";
import { US_STATES } from "@/lib/us-states";

export type SeoLink = { label: string; href: string; rel?: string };

const CITY_SLUG = (city: string, state: string) =>
  `${citySlug(city)}-${(state || "").toLowerCase()}`;

const SPEC_SLUG = (s: string) => specialtySlug(s || "");

/**
 * Convert a 2-letter state code (e.g. "TX") to the full-name slug used by the
 * /locations/$state routes (e.g. "texas"). The locations routes set their
 * canonical to `/locations/${stateNameSlug(state.name)}`, so every internal
 * link must use the same form — the 2-letter code resolves (the route falls
 * back to code matching) but creates a non-canonical duplicate URL with no
 * redirect, diluting link equity.
 *
 * Exported so provider pages and other routes can compute the same slug
 * for breadcrumb links and related-search anchors without duplicating the
 * lookup logic.
 */
export function stateSlugFromCode(code: string): string {
  const upper = (code || "").toUpperCase();
  const st = US_STATES.find((s) => s.code === upper);
  return st ? stateNameSlug(st.name) : code.toLowerCase();
}

// ---------------- Provider profile links ----------------
export type ProviderLinkInput = {
  id: string;
  specialty?: string | null;
  city?: string | null;
  state?: string | null;
  insurance?: string[] | null;
  nearbyCities?: string[]; // pre-resolved
  relatedSpecialties?: string[]; // pre-resolved
};

export function getProviderProfileLinks(p: ProviderLinkInput): {
  sameSpecialtySameCity: SeoLink | null;
  sameSpecialtyNearby: SeoLink[];
  sameInsuranceSameCity: SeoLink[];
  relatedSpecialties: SeoLink[];
  cityHub: SeoLink | null;
  stateHub: SeoLink | null;
  specialtyHub: SeoLink | null;
  insuranceHubs: SeoLink[];
  claim: SeoLink;
} {
  const spec = (p.specialty || "").trim();
  const city = (p.city || "").trim();
  const state = (p.state || "").trim();

  const sameSpecialtySameCity: SeoLink | null =
    spec && city && state
      ? {
          label: `${spec}s in ${city}, ${state.toUpperCase()}`,
          href: `/specialty/${SPEC_SLUG(spec)}/${CITY_SLUG(city, state)}`,
        }
      : null;

  const sameSpecialtyNearby: SeoLink[] =
    spec && state
      ? (p.nearbyCities ?? []).slice(0, 6).map((c) => ({
          label: `${spec}s in ${c}, ${state.toUpperCase()}`,
          href: `/specialty/${SPEC_SLUG(spec)}/${CITY_SLUG(c, state)}`,
        }))
      : [];

  const sameInsuranceSameCity: SeoLink[] =
    city && state
      ? (p.insurance ?? []).slice(0, 5).map((ins) => ({
          label: `Providers accepting ${ins} in ${city}, ${state.toUpperCase()}`,
          href: `/insurance/${SPEC_SLUG(ins)}/${CITY_SLUG(city, state)}`,
        }))
      : [];

  const relatedSpecialties: SeoLink[] = (p.relatedSpecialties ?? [])
    .slice(0, 6)
    .map((s) => ({
      label:
        city && state
          ? `${s}s in ${city}, ${state.toUpperCase()}`
          : `Find a ${s}`,
      href:
        city && state
          ? `/specialty/${SPEC_SLUG(s)}/${CITY_SLUG(city, state)}`
          : `/specialties/${SPEC_SLUG(s)}`,
    }));

  // /locations/$state/$city is a two-segment route. The $state param uses the
  // full state name slug ("texas", not "tx") as its canonical form. The $city
  // param is just the city slug with no state suffix. Using the 2-letter code
  // works (the route falls back to code matching) but creates a non-canonical
  // duplicate with no redirect, diluting link equity across all 6.3M provider
  // pages that render these links.
  const stSlug = stateSlugFromCode(state);
  const cityHub: SeoLink | null =
    city && state
      ? {
          label: `Healthcare providers in ${city}, ${state.toUpperCase()}`,
          href: `/locations/${stSlug}/${citySlug(city)}`,
        }
      : null;

  const stateHub: SeoLink | null = state
    ? { label: `Healthcare providers in ${state.toUpperCase()}`, href: `/locations/${stSlug}` }
    : null;

  const specialtyHub: SeoLink | null = spec
    ? { label: `All ${spec}s`, href: `/specialties/${SPEC_SLUG(spec)}` }
    : null;

  const insuranceHubs: SeoLink[] = (p.insurance ?? []).slice(0, 3).map((ins) => ({
    label: `Providers accepting ${ins}`,
    href: `/insurance/${SPEC_SLUG(ins)}`,
  }));

  // Real route is /providers/$id/claim (providers_.$id.claim.tsx).
  // /claim-profile/$id does not exist and was 404ing on every provider page.
  const claim: SeoLink = {
    label: "Claim this profile",
    href: `/providers/${p.id}/claim`,
    rel: "nofollow",
  };

  return {
    sameSpecialtySameCity,
    sameSpecialtyNearby,
    sameInsuranceSameCity,
    relatedSpecialties,
    cityHub,
    stateHub,
    specialtyHub,
    insuranceHubs,
    claim,
  };
}

// ---------------- Specialty + city page links ----------------
export function getSpecialtyCityLinks(
  specialty: string,
  city: string,
  state: string,
  opts?: {
    nearbyCities?: string[];
    relatedSpecialties?: string[];
    insuranceCarriers?: string[];
  },
): SeoLink[] {
  const out: SeoLink[] = [];
  const spec = SPEC_SLUG(specialty);
  const cs = CITY_SLUG(city, state);
  const stSlug = stateSlugFromCode(state);

  for (const c of (opts?.nearbyCities ?? []).slice(0, 8)) {
    out.push({
      label: `${specialty}s in ${c}, ${state.toUpperCase()}`,
      href: `/specialty/${spec}/${CITY_SLUG(c, state)}`,
    });
  }
  for (const s of (opts?.relatedSpecialties ?? []).slice(0, 6)) {
    out.push({
      label: `${s}s in ${city}, ${state.toUpperCase()}`,
      href: `/specialty/${SPEC_SLUG(s)}/${cs}`,
    });
  }
  for (const ins of (opts?.insuranceCarriers ?? []).slice(0, 6)) {
    out.push({
      label: `${specialty}s in ${city} accepting ${ins}`,
      href: `/insurance/${SPEC_SLUG(ins)}/${cs}`,
    });
  }
  out.push(
    { label: `${specialty} overview`, href: `/specialties/${spec}` },
    { label: `Healthcare in ${city}, ${state.toUpperCase()}`, href: `/locations/${stSlug}/${citySlug(city)}` },
    { label: `Healthcare in ${state.toUpperCase()}`, href: `/locations/${stSlug}` },
    { label: "Healthcare directory", href: "/landing-pages/directory/1" },
  );
  return out;
}

// ---------------- Insurance hub links ----------------
export function getInsuranceLinks(
  insurance: string,
  opts?: { topSpecialties?: string[]; topCities?: Array<{ city: string; state: string }> },
): SeoLink[] {
  const out: SeoLink[] = [];
  const ins = SPEC_SLUG(insurance);
  for (const s of (opts?.topSpecialties ?? []).slice(0, 10)) {
    out.push({ label: `${s}s accepting ${insurance}`, href: `/specialties/${SPEC_SLUG(s)}` });
  }
  for (const c of (opts?.topCities ?? []).slice(0, 12)) {
    out.push({
      label: `${insurance} providers in ${c.city}, ${c.state.toUpperCase()}`,
      href: `/insurance/${ins}/${CITY_SLUG(c.city, c.state)}`,
    });
  }
  return out;
}

// ---------------- City hub links ----------------
export function getCityHubLinks(
  city: string,
  state: string,
  opts?: { topSpecialties?: string[]; commonInsurance?: string[]; nearbyCities?: string[] },
): SeoLink[] {
  const out: SeoLink[] = [];
  const cs = CITY_SLUG(city, state);
  const stSlug = stateSlugFromCode(state);
  for (const s of (opts?.topSpecialties ?? []).slice(0, 12)) {
    out.push({
      label: `${s}s in ${city}, ${state.toUpperCase()}`,
      href: `/specialty/${SPEC_SLUG(s)}/${cs}`,
    });
  }
  for (const ins of (opts?.commonInsurance ?? []).slice(0, 6)) {
    out.push({
      label: `Doctors in ${city} accepting ${ins}`,
      href: `/insurance/${SPEC_SLUG(ins)}/${cs}`,
    });
  }
  for (const c of (opts?.nearbyCities ?? []).slice(0, 6)) {
    out.push({
      label: `Healthcare in ${c}, ${state.toUpperCase()}`,
      href: `/locations/${stSlug}/${citySlug(c)}`,
    });
  }
  out.push({
    label: `All providers in ${state.toUpperCase()}`,
    href: `/locations/${stSlug}`,
  });
  return out;
}

export function getStateHubLinks(
  state: string,
  opts?: { topCities?: string[]; topSpecialties?: string[] },
): SeoLink[] {
  const out: SeoLink[] = [];
  const stSlug = stateSlugFromCode(state);
  for (const c of (opts?.topCities ?? []).slice(0, 20)) {
    out.push({ label: `Healthcare in ${c}, ${state.toUpperCase()}`, href: `/locations/${stSlug}/${citySlug(c)}` });
  }
  for (const s of (opts?.topSpecialties ?? []).slice(0, 10)) {
    out.push({ label: `${s}s in ${state.toUpperCase()}`, href: `/specialty/${SPEC_SLUG(s)}/doctors/${stateNameSlug(US_STATES.find(st => st.code === state.toUpperCase())?.name ?? state)}` });
  }
  return out;
}
